import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import CreateRoomCategory from "./CreateRoomCategory";
import {
  ActiveInactiveRoomCategory,
  deleteRoomCategory,
  getAllRoomCategories,
} from "./services/roomCategory.services";
import {
  clearRoomCategoryDetails,
  clearRoomCategoryID,
  setRoomCategoryID,
} from "./roomCategorySlice";
import AppDrawer, {
  ButtonCreate,
  ButtonEdit,
  LinkEdit,
} from "../../components/drawer/Drawer";

const RoomCategory: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDrawerOpen = useAppSelector((state) => state.drawer.open);
  const drawerName = useAppSelector((state) => state.drawer.name);

  useEffect(() => {
    dispatch(getAllRoomCategories());
    return () => {
      dispatch(clearRoomCategoryDetails());
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteRoomCategoryDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Room Category List
          </Typography.h4>
          <ButtonCreate
            label="Create Room Category"
            drawerName="roomCategoryForm"
            size="default"
            title="Create Room Category"
          />
        </GridLayoutItem>
        <GridLayoutItem>
          <RoomCategoryGridComponent />
        </GridLayoutItem>
        {isDrawerOpen && drawerName === "roomCategoryForm" && (
          <AppDrawer>
            <CreateRoomCategory />
          </AppDrawer>
        )}
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const RoomCategoryGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const RoomCategoryList = useAppSelector(
    (state) => state.roomCategory.RoomCategoryList
  );
  const loading = useAppSelector((state) => state.roomCategory.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? RoomCategoryList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteRoomCategory"));
      dispatch(setRoomCategoryID(ID));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        <ButtonEdit
          ID={props.dataItem.ID}
          name="roomCategoryForm"
          title="Update Room Category"
        />
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
        >
          <MdDelete
            className="absolute-position"
            style={{ fontSize: "24px" }}
          />
        </IconButton>
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveRoomCategory(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllRoomCategories());
        } else {
          dispatch(getAllRoomCategories());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Switch
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {RoomCategoryList && RoomCategoryList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(RoomCategoryList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : RoomCategoryList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={RoomCategoryList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="RoomCategory"
            title="Room Category"
            cell={(props: any) => (
              <td className="hoverableName">
                <LinkEdit
                  ID={props.dataItem.ID}
                  name="roomCategoryForm"
                  title="Update Room Category"
                  masterName={props.dataItem?.RoomCategory}
                />
              </td>
            )}
          />
          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          <Column
            field="ID"
            title="Actions"
            cell={MyEditCommandCell}
            width={"110px"}
            filterable={false}
          />
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteRoomCategoryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const RoomCategoryID = useAppSelector(
    (state) => state.roomCategory.RoomCategoryID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteRoomCategory(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllRoomCategories());
        dispatch(closeDialog());
        dispatch(clearRoomCategoryID());
      } else {
        dispatch(getAllRoomCategories());
        dispatch(closeDialog());
        dispatch(clearRoomCategoryID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteRoomCategory" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Room Category"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Room Category?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(RoomCategoryID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default RoomCategory;
